import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';

const CinemaSettings = ({ adminToken }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cinema/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Error loading cinema settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/admin/cinema/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        setMessage('Gespeichert!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setMessage('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const updateGenre = (index, field, value) => {
    const updated = { ...settings };
    updated.genres = [...updated.genres];
    updated.genres[index] = { ...updated.genres[index], [field]: value };
    setSettings(updated);
  };

  const addGenre = () => {
    const updated = { ...settings };
    updated.genres = [{
      key: `genre_${Date.now()}`,
      label: 'Neues Genre',
      emoji: '',
      template: '',
      is_active: true,
      sort_order: 0
    }, ...updated.genres];
    updated.genres.forEach((g, i) => { g.sort_order = i + 1; });
    setSettings(updated);
  };

  const removeGenre = (index) => {
    const updated = { ...settings };
    updated.genres = updated.genres.filter((_, i) => i !== index);
    setSettings(updated);
  };

  const moveGenre = (index, direction) => {
    const updated = { ...settings };
    updated.genres = [...updated.genres];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= updated.genres.length) return;
    [updated.genres[index], updated.genres[newIndex]] = [updated.genres[newIndex], updated.genres[index]];
    updated.genres.forEach((g, i) => { g.sort_order = i + 1; });
    setSettings(updated);
  };

  if (loading) return <div className="text-white/60 p-8">Lade Cinema Settings...</div>;
  if (!settings) return <div className="text-red-400 p-8">Fehler beim Laden</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Kino-Modus Settings</h2>
        <div className="flex items-center gap-3">
          {message && (
            <span className={`text-sm ${message.includes('Fehler') ? 'text-red-400' : 'text-emerald-400'}`}>
              {message}
            </span>
          )}
          <button
            onClick={saveSettings}
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Genres */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-white/70 uppercase tracking-wider">Genres</h3>
          <button
            onClick={addGenre}
            className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white/80 rounded text-xs"
          >
            + Genre hinzufügen
          </button>
        </div>

        {settings.genres.map((genre, index) => (
          <div key={genre.key} className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              {/* Sortierung */}
              <div className="flex flex-col gap-1">
                <button onClick={() => moveGenre(index, -1)} className="text-white/40 hover:text-white text-xs">▲</button>
                <button onClick={() => moveGenre(index, 1)} className="text-white/40 hover:text-white text-xs">▼</button>
              </div>

              {/* Emoji - ausgeblendet, Daten bleiben erhalten */}
              {/* <input
                type="text"
                value={genre.emoji}
                onChange={(e) => updateGenre(index, 'emoji', e.target.value)}
                className="w-12 bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-center text-lg"
              /> */}

              {/* Key */}
              <input
                type="text"
                value={genre.key}
                onChange={(e) => updateGenre(index, 'key', e.target.value)}
                className="w-28 bg-white/10 border border-white/20 rounded px-2 py-1 text-white/60 text-xs font-mono"
                placeholder="key"
              />

              {/* Label */}
              <input
                type="text"
                value={genre.label}
                onChange={(e) => updateGenre(index, 'label', e.target.value)}
                className="flex-1 bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
                placeholder="Label"
              />

              {/* Aktiv Toggle */}
              <button
                onClick={() => updateGenre(index, 'is_active', !genre.is_active)}
                className={`px-3 py-1 rounded text-xs font-medium ${
                  genre.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}
              >
                {genre.is_active ? 'Aktiv' : 'Inaktiv'}
              </button>

              {/* Löschen */}
              <button
                onClick={() => removeGenre(index)}
                className="text-red-400/60 hover:text-red-400 text-sm"
              >
                ✕
              </button>
            </div>

            {/* Template */}
            <textarea
              value={genre.template}
              onChange={(e) => updateGenre(index, 'template', e.target.value)}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white/80 text-sm resize-y"
              placeholder="Genre-Template für die KI-Analyse..."
            />
          </div>
        ))}
      </div>

      {/* Basis-Anweisungen */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-white/70 uppercase tracking-wider">Basis-Anweisungen</h3>
        <textarea
          value={settings.base_instructions || ''}
          onChange={(e) => setSettings({ ...settings, base_instructions: e.target.value })}
          rows={8}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white/80 text-sm resize-y"
          placeholder="Regeln die für ALLE Genres gelten..."
        />
      </div>

      {/* Wortanzahl */}
      <div className="flex gap-4">
        <div className="space-y-1">
          <label className="text-xs text-white/50">Wörter Min</label>
          <input
            type="number"
            value={settings.word_count_min || 150}
            onChange={(e) => setSettings({ ...settings, word_count_min: parseInt(e.target.value) || 150 })}
            className="w-24 bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-white/50">Wörter Max</label>
          <input
            type="number"
            value={settings.word_count_max || 300}
            onChange={(e) => setSettings({ ...settings, word_count_max: parseInt(e.target.value) || 300 })}
            className="w-24 bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
          />
        </div>
      </div>
    </div>
  );
};

export default CinemaSettings;
